import { useEffect, useId, useState } from "react";
import { Trans } from "@lingui/macro";
import { useLingui } from "@lingui/react";
import { Checkbox } from "@/components/ui/checkbox";
import { patientIntakeApi } from "@/lib/patientIntakeApi";
import type { IntakeData } from "@/lib/types";
import {
  FIELD,
  disclosureLocale,
  displayChecked,
  decideAfterLoad,
  shouldFocusAfterLoad,
  splitUrls,
} from "@/lib/buildConsents";

type Channel = "sms" | "voice";
type Disclosure = { id: string; bodyText: string; locale?: string };
export type ConsentState =
  | { status: "loading" }
  | { status: "error" }
  | { status: "ready"; disclosure: Disclosure };

// One fetch per channel+locale even when several instances mount (e.g. mobile + desktop layouts).
// Failures are evicted so a remount can retry.
const inflight = new Map<string, Promise<Disclosure>>();
function loadDisclosure(
  channel: Channel,
  locale: "en" | "es",
): Promise<Disclosure> {
  const key = `${channel}:${locale}`;
  let p = inflight.get(key);
  if (!p) {
    p = patientIntakeApi.getCurrentDisclosure(channel, locale).then((d) => {
      if (!d) throw new Error("bad disclosure");
      return { id: d.id, bodyText: d.bodyText, locale: d.locale };
    });
    inflight.set(key, p);
    p.catch(() => inflight.delete(key));
    // Don't keep a resolved disclosure past this tick: versions can change server-side.
    p.then(() => setTimeout(() => inflight.delete(key), 1000)).catch(() => {});
  }
  return p;
}

function ExternalLink({
  href,
  children,
}: {
  href: string;
  children: React.ReactNode;
}) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="font-medium text-[#166B93] underline decoration-[#166B93]/30 underline-offset-2 transition-colors hover:decoration-[#166B93] focus-visible:rounded-sm focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#166B93]"
    >
      {href === "https://www.jemaservices.com.co/terms-of-service" ? (
        <Trans>Terms of Service</Trans>
      ) : href === "https://www.jemaservices.com.co/privacy-policy" ? (
        <Trans>Privacy Policy</Trans>
      ) : (
        children
      )}
      <span className="sr-only">
        {" "}
        <Trans>(opens in a new tab)</Trans>
      </span>
    </a>
  );
}

function DisclosureText({
  collapsible,
  children,
}: {
  collapsible: boolean;
  children: React.ReactNode;
}) {
  if (!collapsible)
    return <div className="mb-2 text-xs leading-relaxed">{children}</div>;
  return (
    <details className="mb-2 text-xs leading-relaxed">
      <summary className="w-fit cursor-pointer py-1 font-medium text-[#166B93] underline underline-offset-2 focus-visible:outline-2 focus-visible:outline-offset-2">
        <Trans>Consent details</Trans>
      </summary>
      {children}
    </details>
  );
}

/** Presentational box; exported so loading/ready/error states can be rendered without a network. */
export function ConsentBoxView({
  state,
  checked,
  cleared,
  label,
  onToggle,
  onRetry,
  retrying = false,
  retryPending = false,
  onFocused,
  requestedLocale,
  collapsible = false,
}: {
  collapsible?: boolean;
  onRetry?: () => void;
  /** A retry is in flight: keep the button mounted (aria-disabled) so keyboard focus is not lost. */
  retrying?: boolean;
  /** After a successful retry, move focus to the checkbox (the button unmounts). */
  retryPending?: boolean;
  onFocused?: () => void;
  requestedLocale?: string;
  state: ConsentState;
  checked: boolean;
  cleared: false | "stale" | "error";
  label: React.ReactNode;
  onToggle: (on: boolean) => void;
}) {
  const uid = useId();
  const ready = state.status === "ready";
  const retryId = `${uid}-retry`;
  useEffect(() => {
    if (shouldFocusAfterLoad({ retryPending, status: state.status })) {
      document.getElementById(uid)?.focus();
      onFocused?.(); // one-shot: later reloads (e.g. locale change) must not steal focus
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [retryPending, state.status, uid]);
  return (
    <div
      className={`contact-consent-option rounded-lg border px-3 py-1 transition-colors duration-150 ${checked ? "border-[#8bb8ca] bg-[#f1f8fa]" : "border-slate-200 bg-white"}`}
    >
      <div className="flex items-center">
        <Checkbox
          id={uid}
          checked={checked}
          disabled={!ready}
          aria-describedby={`${uid}-desc`}
          onCheckedChange={(c) => ready && onToggle(c === true)}
          className="contact-consent-checkbox"
        />
        <label
          id={`${uid}-label`}
          htmlFor={uid}
          className="flex min-h-11 flex-1 cursor-pointer items-center py-1 text-[13px] font-semibold leading-5 text-slate-800"
        >
          {label}
        </label>
      </div>
      <div>
        <div
          id={`${uid}-desc`}
          className="text-[13px] leading-[1.65] text-slate-600"
        >
          {/* Always mounted so loading/error text is announced when filled in. */}
          <p role="status" aria-live="polite" className="empty:hidden">
            {state.status === "loading" && <Trans>Loading...</Trans>}
            {state.status === "error" && (
              <Trans>
                This option is unavailable right now. You can continue without
                it.
              </Trans>
            )}
          </p>
          {ready &&
            !!requestedLocale &&
            !!state.disclosure.locale &&
            state.disclosure.locale.slice(0, 2).toLowerCase() !==
              requestedLocale && (
              <p className="mb-1 font-medium">
                <Trans>The text below is available in English only.</Trans>
              </p>
            )}
          {ready && (
            <DisclosureText collapsible={collapsible}>
              <p className="whitespace-pre-line [overflow-wrap:anywhere]">
                {splitUrls(
                  state.disclosure.bodyText
                    .replace(
                      /Terms of Service\s*\((https:\/\/www\.jemaservices\.com\.co\/terms-of-service)\)/g,
                      "$1",
                    )
                    .replace(
                      /Privacy Policy\s*\((https:\/\/www\.jemaservices\.com\.co\/privacy-policy)\)/g,
                      "$1",
                    ),
                ).map((part, i) =>
                  part.url ? (
                    <ExternalLink key={i} href={part.url}>
                      {part.text}
                    </ExternalLink>
                  ) : (
                    <span key={i}>{part.text}</span>
                  ),
                )}
              </p>
            </DisclosureText>
          )}
        </div>
        {/* Outside the aria-describedby element so its text is not read as part of the description. */}
        {onRetry &&
          (state.status === "error" ||
            (retrying && state.status === "loading")) && (
            <button
              type="button"
              id={retryId}
              aria-labelledby={`${retryId} ${uid}-label`}
              aria-disabled={retrying || undefined}
              onClick={() => !retrying && onRetry()}
              className="mt-2 text-[13px] font-semibold text-[#166B93] underline underline-offset-2 aria-disabled:opacity-60"
            >
              <Trans>Try again</Trans>
            </button>
          )}
        {/* Always mounted so screen readers announce the text when it is filled in. */}
        <p
          role="status"
          aria-live="polite"
          className="mt-2 text-[13px] leading-5 text-amber-800 empty:hidden"
        >
          {cleared === "stale" && (
            <Trans>
              Please review the updated wording and tick again if you still want
              these messages.
            </Trans>
          )}
          {cleared === "error" && (
            <Trans>
              We could not load the wording, so your selection was cleared.
              Please try again.
            </Trans>
          )}
        </p>
      </div>
    </div>
  );
}

function ConsentBox({
  channel,
  data,
  onUpdate,
  label,
}: {
  channel: Channel;
  data: IntakeData;
  onUpdate: (patch: Partial<IntakeData>) => void;
  label: React.ReactNode;
}) {
  const { i18n } = useLingui();
  const locale = disclosureLocale(i18n.locale);
  const field = FIELD[channel];
  const ticked = data[field]?.disclosureId;
  const [state, setState] = useState<ConsentState>({ status: "loading" });
  const [cleared, setCleared] = useState<false | "stale" | "error">(false);
  const [attempt, setAttempt] = useState(0);
  const [retryPending, setRetryPending] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setState({ status: "loading" });
    setCleared(false); // a notice from an earlier load must never sit beside a freshly loaded box
    const settle = (loadedId: string | undefined) => {
      const { clear, notice } = decideAfterLoad({ ticked, loadedId });
      if (clear) {
        onUpdate({ [field]: null });
        setCleared(notice === "none" ? false : notice);
      }
    };
    loadDisclosure(channel, locale)
      .then((d) => {
        if (cancelled) return;
        setState({ status: "ready", disclosure: d });
        settle(d.id);
      })
      .catch(() => {
        if (cancelled) return;
        setState({ status: "error" });
        setRetryPending(false); // failed retry: keep the button, no focus move
        settle(undefined);
      });
    return () => {
      cancelled = true;
    };
    // ticked/onUpdate intentionally omitted: refetch only on channel/locale change or explicit retry.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [channel, locale, attempt]);

  const retry = () => {
    inflight.delete(`${channel}:${locale}`); // bust any failed/in-flight entry; never ticks anything
    setRetryPending(true);
    setAttempt((n) => n + 1);
  };

  const ready = state.status === "ready";
  return (
    <ConsentBoxView
      state={state}
      collapsible={false}
      // What the patient sees must equal what is sent: a stored tick shows as ticked (disabled) while loading.
      checked={displayChecked(
        ticked,
        state.status === "ready" ? state.disclosure.id : undefined,
        ready,
      )}
      cleared={ticked ? false : cleared}
      requestedLocale={locale}
      onRetry={retry}
      retrying={attempt > 0 && state.status === "loading"}
      retryPending={retryPending}
      onFocused={() => setRetryPending(false)}
      label={label}
      onToggle={(on) =>
        ready &&
        onUpdate({ [field]: on ? { disclosureId: state.disclosure.id } : null })
      }
    />
  );
}

/** Two optional, unchecked-by-default contact consents; never block Next/Submit. Render beside the phone field. */
export function ContactConsents({
  data,
  onUpdate,
}: {
  data: IntakeData;
  onUpdate: (patch: Partial<IntakeData>) => void;
}) {
  return (
    <fieldset className="contact-consents mt-4 min-w-0 space-y-2 border-0 p-0">
      <legend className="mb-1 flex w-full items-center justify-between text-[13px] font-semibold text-slate-700">
        <Trans>Contact preferences</Trans>
        <span className="text-xs font-normal text-slate-500">
          <Trans>Optional</Trans>
        </span>
      </legend>
      <ConsentBox
        channel="sms"
        data={data}
        onUpdate={onUpdate}
        label={<Trans>Jema Services care updates & marketing texts (SMS)</Trans>}
      />
      <ConsentBox
        channel="voice"
        data={data}
        onUpdate={onUpdate}
        label={
          <span>
            <Trans>
              Jema Services care & marketing calls (including AI voice)
            </Trans>
          </span>
        }
      />
    </fieldset>
  );
}
